<template>
  <VMenu v-if="shouldShow" theme="lookdeep-ai" :delay="{ show: 0, hide: 200 }">
    <img src="@/assets/icons/ai-summary.svg" class="summary-icon"/>
    <template #popper>
      <p>{{ currentAiSnapshot }}</p>
      <div class="ai-snapshot-timestamp">
        <button class="refresh-button" @click="refreshSnapshot" v-if="canRefreshSnapshot">
          <b-loading v-if="isRefreshing" :is-full-page="false" v-model="isRefreshing" :can-cancel="false"></b-loading>
          <i v-else class="mdi mdi-refresh"></i>
        </button>
        <span>Last updated: {{ $helpers.formatDate(currentAiSnapshotUpdatedAt) }}</span>
      </div>
      <div class="ai-feedback-actions">
        <user-feedback-buttons feature="crystal_ball" :metadata="{ patient_id: patientId, ai_snapshot: currentAiSnapshot }" />
      </div>
    </template>
  </VMenu>
</template>

<script>
import to from '@/lib/to'
import {SERVER_EVENT, CHANNEL} from '@/lib/Pusher'
import {isVideoDeviceOffline} from '@/lib/PatientUtils'
import UserFeedbackButtons from '@/components/UserFeedbackButtons.vue'

export default {
  name: 'CrystalBall',
  components: { UserFeedbackButtons },
  props: {
    patientId: {
      type: Number,
      required: true
    },
    patientMonitor: {
      type: Object,
      default: null
    },
    aiSnapshot: {
      type: String,
      default: null
    },
    aiSnapshotUpdatedAt: {
      type: String,
      default: null
    }
  },
  beforeUnmount() {
    this.hospitalChannel?.unbind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  },
  mounted() {
    this.hospitalChannel = this.$pusher.subscribe(CHANNEL.FOR_HOSPITAL({
      env: import.meta.env.VITE_ENVIRONMENT_NAME,
      hospital: this.$store.state.currentHospital
    }))

    this.hospitalChannel.bind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  },
  computed: {
    shouldShow() {
      return this.currentAiSnapshot && this.isMonitorActive
    },
    isMonitorActive() {
      return this.patientMonitor && this.patientMonitor.status === 'active'
    },
    canRefreshSnapshot() {
      if (!this.patientMonitor || !this.patientMonitor.id || !this.isMonitorActive) {
        return false
      }
      const videoDevice = this.patientMonitor.video_device
      if (videoDevice) {
        return !isVideoDeviceOffline({ video_device: videoDevice })
      }
      return true
    },
    currentAiSnapshot() {
      return this.localAiSnapshot || this.aiSnapshot
    },
    currentAiSnapshotUpdatedAt() {
      return this.localAiSnapshotUpdatedAt || this.aiSnapshotUpdatedAt
    }
  },
  methods: {
    async refreshSnapshot() {
      if (!this.patientMonitor || !this.patientMonitor.id || this.isRefreshing) {
        return
      }
      this.isRefreshing = true
      const [err] = await to(this.$http.post(`/patient_monitors/${this.patientMonitor.id}/refresh_snapshot`, {}, { skipLoading: true }))

      if (err) {
        this.isRefreshing = false
        this.$buefy.toast.open({
          message: 'Failed to refresh snapshot',
          type: 'is-danger',
          duration: 3000
        })
      }
    },
    handleAiSnapshotUpdated(data) {
      if (data.patient_id === this.patientId) {
        this.localAiSnapshot = data.ai_snapshot
        this.localAiSnapshotUpdatedAt = data.ai_snapshot_updated_at
        this.isRefreshing = false
      }
    }
  },
  data() {
    return {
      hospitalChannel: null,
      localAiSnapshot: null,
      localAiSnapshotUpdatedAt: null,
      isRefreshing: false
    }
  }
}
</script>

<style scoped lang="scss">
.summary-icon {
  width: 24px;
}
.ai-snapshot-timestamp {
  font-size: 12px;
  opacity: 0.8;
  padding-top: 5px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
}

.refresh-button {
  border: none;
  background: none;
  color: var(--ld-text-inverse);
  cursor: pointer;
  padding-top: 2px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:not(:disabled):hover {
    color: var(--ld-text-tertiary);
  }
  i {
    font-size: 18px;
  }

  .loading-overlay {
    position: static;
    padding-bottom: 4px;
    padding-top: 3px;
    :deep(.loading-background) {
      display: none;
    }
    :deep(.loading-icon::after) {
      position: static;
      width: 15px;
      height: 15px;
      border-width: 2px;
      padding: 5px;
    }
  }
}

.ai-feedback-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--ld-text-inverse);
}
</style>
